import React, { useCallback, useMemo } from 'react';
import { path } from 'ramda';
import moment from 'moment';
import {
  DateTimePicker,
  TimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { makeStyles } from '@mui/styles';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { TextField, Grid, IconButton } from '@mui/material';
import { Controller } from 'react-hook-form';

import NestedSelect from '../../NestedSelect';
import {
  INCLUDE_EXCLUDE_OPTIONS,
  CRITERIA_OPTIONS,
  OPERATORS,
  CRITERIA_TYPE,
} from './constants';
import { CRITERIA_CONFIGS } from './config';
import palette from '../../styles/palette';
import { NO_OPTIONS } from '../../../constants/strings';

const getOrExecute = (valueOrFunction, ...args) =>
  typeof valueOrFunction === 'function'
    ? valueOrFunction(...args)
    : valueOrFunction;

const useStyles = makeStyles(({ typography }) => ({
  button: {
    color: palette.blue,
    marginLeft: typography.pxToRem(10),
  },
  container: {
    marginTop: typography.pxToRem(2),
    marginBottom: typography.pxToRem(8),
    maxWidth: typography.pxToRem(820),
  },
  timePickerPopover: {
    '& .MuiButton-root': {
      height: 'auto',
    },
  },
}));

const CriteriaItem = ({
  onAddClick,
  onRemoveClick,
  prefixName,
  route,
  value,
  onValueChange,
  last,
  first,
  control,
}) => {
  const classes = useStyles();

  const criteriaConfig = CRITERIA_CONFIGS[value.criteria];
  const criteriaWithOperators = path(['operators'], criteriaConfig);
  const criteriaType = path(['type'], criteriaConfig);

  const valueName = `${prefixName}.value`;

  const createOnChange = useCallback(
    (fieldName) => (value) => {
      onValueChange(fieldName, value);
    },
    [onValueChange]
  );

  const onChangeCriteria = useCallback(
    (criteria) => {
      const defaultValue =
        CRITERIA_CONFIGS[criteria].type === CRITERIA_TYPE.DATETIME
          ? moment().toISOString()
          : getOrExecute(CRITERIA_CONFIGS[criteria].default, route);

      let newValue = {
        ...value,
        criteria,
        value: defaultValue,
        operator: CRITERIA_CONFIGS[criteria].operators ? '>' : undefined,
      };

      onValueChange('criteria', newValue);
    },
    [onValueChange, prefixName, route, value]
  );

  const options = useMemo(
    () => getOrExecute(criteriaConfig?.options, route),
    [criteriaConfig, route]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={2}>
          <Controller
            name={`${prefixName}.rule`}
            control={control}
            render={({ field }) => (
              <NestedSelect
                {...field}
                value={field.value}
                options={INCLUDE_EXCLUDE_OPTIONS}
                onChange={(val) => {
                  field.onChange(val);
                  createOnChange(`$rule`)(val);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <Controller
            name={`${prefixName}.criteria`}
            control={control}
            render={({ field }) => (
              <NestedSelect
                {...field}
                value={field.value}
                options={CRITERIA_OPTIONS}
                onChange={(val) => {
                  field.onChange(val);
                  onChangeCriteria(val);
                }}
              />
            )}
          />
        </Grid>
        {criteriaWithOperators && (
          <Grid item xs={2}>
            <Controller
              name={`${prefixName}.operator`}
              control={control}
              render={({ field }) => (
                <NestedSelect
                  {...field}
                  value={field.value}
                  options={OPERATORS}
                  onChange={(val) => {
                    field.onChange(val);
                    createOnChange(`operator`)(val);
                  }}
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs>
          {criteriaType === CRITERIA_TYPE.SELECT && (
            <Controller
              name={valueName}
              control={control}
              render={({ field }) => (
                <NestedSelect
                  {...field}
                  value={field.value}
                  options={options}
                  onChange={(val) => {
                    field.onChange(val);
                    createOnChange('value')(val);
                  }}
                  disabled={!options?.length}
                  placeholder={!options?.length && NO_OPTIONS}
                />
              )}
            />
          )}
          {(criteriaType === CRITERIA_TYPE.NUMBER ||
            criteriaType === CRITERIA_TYPE.STRING) && (
            <Controller
              name={valueName}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={criteriaType}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          )}
          {criteriaType === CRITERIA_TYPE.TIME && (
            <Controller
              name={valueName}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TimePicker
                  label="Select Time"
                  value={value}
                  onChange={onChange}
                  ampm={false}
                  format="HH:mm"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      name={valueName}
                      InputProps={{
                        ...params.InputProps,
                        classes: { popper: classes?.timePickerPopover },
                      }}
                    />
                  )}
                />
              )}
            />
          )}
          {criteriaType === CRITERIA_TYPE.DATETIME && (
            <Controller
              name={valueName}
              control={control}
              render={({ field: { onChange, value } }) => (
                <DateTimePicker
                  value={value}
                  onChange={onChange}
                  ampm
                  format="DD:MM:YYYY, hh:mm A"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      name={valueName}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={2}>
          <IconButton
            size="small"
            onClick={onRemoveClick}
            className={classes.button}
            data-id="add"
            disabled={first && last}
          >
            <RemoveIcon />
          </IconButton>
          {last && (
            <IconButton
              size="small"
              className={classes.button}
              onClick={onAddClick}
              data-id="remove"
            >
              <AddIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default CriteriaItem;
